import i18n from '../locales/i18n';

// Map language codes to locale strings
const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  ja: 'ja-JP',
  he: 'he-IL',
};

// Get current locale from i18n language
export const getCurrentLocale = (): string => {
  const currentLang = i18n.language || 'en';

  return LOCALE_MAP[currentLang] || 'en-US';
};

export const formatCurrency = (amount: number, currency = 'USD', locale?: string): string => {
  const currentLocale = locale || getCurrentLocale();

  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (number: number, locale?: string): string => {
  const currentLocale = locale || getCurrentLocale();

  return new Intl.NumberFormat(currentLocale).format(number);
};

export const formatPercentage = (value: number, locale?: string): string => {
  const currentLocale = locale || getCurrentLocale();

  return new Intl.NumberFormat(currentLocale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
};

export const formatCompactNumber = (number: number, locale?: string): string => {
  const currentLocale = locale || getCurrentLocale();

  return new Intl.NumberFormat(currentLocale, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(number);
};

export const formatDate = (date: Date | string, locale?: string): string => {
  const currentLocale = locale || getCurrentLocale();
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(currentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dateObj);
};

export const formatRelativeTime = (date: Date | string, locale?: string): string => {
  const currentLocale = locale || getCurrentLocale();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(currentLocale, { numeric: 'auto' });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second');
  } else if (diffInSeconds < 3600) {
    return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
  } else if (diffInSeconds < 86400) {
    return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
  } else if (diffInSeconds < 2592000) {
    return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
  } else if (diffInSeconds < 31536000) {
    return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
  } else {
    return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
  }
};
